/**
 * Whether the game makes a noise, and how much of one — remembered between visits.
 *
 * There was no way to turn the sound off. The SETTINGS page has carried a VOLUME slider since
 * the menus were built, under a heading saying that nothing on the page reaches the match, and
 * it did not: the number went into the mix once when the audio engine was created and no
 * control on any screen could move it afterwards. So the only way to play in silence was the
 * browser's own tab mute, which is not a setting the game owns.
 *
 * Kept out of `MatchAudio` and out of the menus alike, for the same reason the key bindings and
 * the learning-mode switch are: the audio engine is created when the first match opens the
 * screen, the setting can be changed from the main menu before any of that exists, and both
 * have to agree. A screen writes here, the engine listens here, and neither holds a reference
 * to the other.
 */

const STORAGE_KEY = 'gf.audio.v1';

export interface AudioPreference {
  /** Silence, without forgetting how loud it was. */
  muted: boolean;
  /** Nothing to full. The original's own config default is a half, and that is what it opens at. */
  volume: number;
}

const FACTORY: AudioPreference = { muted: false, volume: 0.5 };

let current: AudioPreference = { ...FACTORY };
const listeners = new Set<(preference: AudioPreference) => void>();

export function loadAudioPreference(): AudioPreference {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AudioPreference>;
      current = {
        muted: parsed.muted === true,
        volume: typeof parsed.volume === 'number' ? clampVolume(parsed.volume) : FACTORY.volume,
      };
    }
  } catch {
    // Storage can be unavailable and the stored text can be anything at all. The factory
    // setting is a valid answer to both.
    current = { ...FACTORY };
  }
  return current;
}

export function audioPreference(): AudioPreference {
  return current;
}

/**
 * What the engine should actually apply: nothing when it is muted, the chosen loudness
 * otherwise. Both callers want this rather than the two fields, so it is computed in one place.
 */
export function effectiveVolume(preference: AudioPreference = current): number {
  return preference.muted ? 0 : preference.volume;
}

export function setAudioPreference(next: Partial<AudioPreference>): AudioPreference {
  current = {
    muted: next.muted ?? current.muted,
    volume: next.volume === undefined ? current.volume : clampVolume(next.volume),
  };
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Not being able to remember it is no reason to refuse it for this session.
  }
  for (const listener of listeners) listener(current);
  return current;
}

/** Returns the function that stops listening; a match that has ended must not be kept alive. */
export function onAudioPreferenceChange(
  listener: (preference: AudioPreference) => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function clampVolume(volume: number): number {
  return Number.isFinite(volume) ? Math.min(Math.max(volume, 0), 1) : FACTORY.volume;
}
